/* eslint-disable jira/filename-case */
import type { UFOExperience } from '@atlassian/ufo/ufo-experimental';
import type HttpError from '@atlassian/jira-fetch/src/utils/errors.tsx';

export type Marks = {
	[key: string]: string;
};

export type ExperienceMetadata = {
	metadata: {
		[key: string]: string | number | boolean | undefined;
	};
};

type ProtectedMetadataKeys = 'error' | 'errors' | 'traceId';

export type ExperienceMetadataExtra = {
	metadata: {
		// We exclude the following keys from the metadata object to avoid overriding the default metadata
		[key: Exclude<string, ProtectedMetadataKeys>]: string | number | boolean | undefined;
	};
};

export const isExperienceMetadata = (error: ExperienceErrorReason): error is ExperienceMetadata => {
	return typeof error === 'object' && error !== null && 'metadata' in error;
};

export type ExperienceErrorReason = HttpError | Error | ExperienceMetadata | string | undefined;

/**
 * Helper type for the close experience functions. Represents a function that can be called with
 * metadata to end an experience through UFOExperience, e.g. failure or abort.
 */
export type CloseExperienceFunction = (meta?: ExperienceMetadata) => void;

export type JPDExperience<
	ErrorReason extends ExperienceErrorReason = ExperienceErrorReason,
	ExtraMetadata extends ExperienceMetadataExtra = ExperienceMetadataExtra,
> = {
	ufoExperience: UFOExperience;

	start: (startTime?: number) => void;
	mark: (mark: string) => void;
	success: (meta?: ExtraMetadata, startTime?: number) => void;
	successWithReason: (reason?: ErrorReason) => void;
	failure: (reason?: ErrorReason, extraMetadata?: ExtraMetadata) => void;
	abort: (reason?: ErrorReason, extraMetadata?: ExtraMetadata) => void;
};

export type MarkedJPDExperience<TMarks extends Marks> = JPDExperience & {
	marks: TMarks;
};
